import api from '@api';
import { call, put, takeEvery } from 'redux-saga/effects';
import {
    getEgonRouteDetailFetchSuccess,
    getEgonRouteDetailFetchFailure,
    createEgonRouteDetailFetchSuccess,
    createEgonRouteDetailFetchFailure,
    updateEgonRouteDetailFetchSuccess,
    updateEgonRouteDetailFetchFailure,
    deleteEgonRouteDetailFetchSuccess,
    deleteEgonRouteDetailFetchFailure,
} from './reducer';
import { addMessage } from 'src/resources/alertMessages/reducer';
import {
    EgonRouteApiType,
    EgonRouteCreateBodyType,
    EgonRouteDetailResType,
    EgonRouteUpdateBodyType,
    mapEgonRoute,
} from 'src/types/egonRoute';
import { getEgonConflictCountries } from 'src/utils/egonRoutes';
import translate from '@translate';

const getEgonRouteDetailFetchRequest = async (id: string): Promise<EgonRouteDetailResType> => {
    const res = await api<{ data: EgonRouteApiType }>('get', `api/secure/egon-routes/${id}`);
    return { data: mapEgonRoute(res.data.data) };
};

function* getEgonRouteDetailFetch(action: { type: string; payload: string }) {
    try {
        const response: EgonRouteDetailResType = yield call(getEgonRouteDetailFetchRequest, action.payload);
        yield put(getEgonRouteDetailFetchSuccess(response));
    } catch (error) {
        yield put(getEgonRouteDetailFetchFailure({ error }));
    }
}

export function* getEgonRouteDetailFetchWatch() {
    yield takeEvery('egonRouteDetail/getEgonRouteDetailFetch', getEgonRouteDetailFetch);
}

function* putConflictMessage(conflictCountries: string[]) {
    yield put(
        addMessage({
            type: 'error',
            title: translate('egonRouteDetail.message.conflictTitle'),
            text: conflictCountries.length
                ? translate('egonRouteDetail.message.conflictCountries', { countries: conflictCountries.join(', ') })
                : translate('egonRouteDetail.message.conflict'),
        })
    );
}

const createEgonRouteDetailFetchRequest = async (data: EgonRouteCreateBodyType) => {
    const res = await api('post', 'api/secure/egon-routes', data);
    return res.data;
};

function* createEgonRouteDetailFetch(action: { type: string; payload: EgonRouteCreateBodyType }) {
    try {
        const response: unknown = yield call(createEgonRouteDetailFetchRequest, action.payload);
        yield put(
            createEgonRouteDetailFetchSuccess({
                response,
                messageSuccess: { title: translate('egonRouteDetail.message.created') },
            })
        );
    } catch (error) {
        const conflict = getEgonConflictCountries(error);
        if (conflict) {
            yield call(putConflictMessage, conflict);
            yield put(createEgonRouteDetailFetchFailure({ error, noMessage: true }));
        } else {
            yield put(createEgonRouteDetailFetchFailure({ error }));
        }
    }
}

export function* createEgonRouteDetailFetchWatch() {
    yield takeEvery('egonRouteDetail/createEgonRouteDetailFetch', createEgonRouteDetailFetch);
}

const updateEgonRouteDetailFetchRequest = async (params: {
    id: string;
    data: EgonRouteUpdateBodyType;
}): Promise<EgonRouteDetailResType> => {
    const res = await api<{ data: EgonRouteApiType }>('put', `api/secure/egon-routes/${params.id}`, params.data);
    return { data: mapEgonRoute(res.data.data) };
};

function* updateEgonRouteDetailFetch(action: {
    type: string;
    payload: { id: string; data: EgonRouteUpdateBodyType };
}) {
    try {
        const response: EgonRouteDetailResType = yield call(updateEgonRouteDetailFetchRequest, action.payload);
        yield put(
            updateEgonRouteDetailFetchSuccess({
                ...response,
                messageSuccess: { title: translate('egonRouteDetail.message.updated') },
            })
        );
    } catch (error) {
        const conflict = getEgonConflictCountries(error);
        if (conflict) {
            yield call(putConflictMessage, conflict);
            yield put(updateEgonRouteDetailFetchFailure({ error, noMessage: true }));
        } else {
            yield put(updateEgonRouteDetailFetchFailure({ error }));
        }
    }
}

export function* updateEgonRouteDetailFetchWatch() {
    yield takeEvery('egonRouteDetail/updateEgonRouteDetailFetch', updateEgonRouteDetailFetch);
}

const deleteEgonRouteDetailFetchRequest = async (id: string) => {
    const res = await api('delete', `api/secure/egon-routes/${id}`);
    return res.data;
};

function* deleteEgonRouteDetailFetch(action: { type: string; payload: string }) {
    try {
        yield call(deleteEgonRouteDetailFetchRequest, action.payload);
        yield put(
            deleteEgonRouteDetailFetchSuccess({
                messageSuccess: { title: translate('egonRouteDetail.message.deleted') },
            })
        );
    } catch (error) {
        yield put(deleteEgonRouteDetailFetchFailure({ error }));
    }
}

export function* deleteEgonRouteDetailFetchWatch() {
    yield takeEvery('egonRouteDetail/deleteEgonRouteDetailFetch', deleteEgonRouteDetailFetch);
}
